import React from 'react';
import { useTranslation } from 'react-i18next';
import FormSelect from './formSelect';

/**
 * StyledSelect — a drop-in replacement for react-bootstrap's native <FormSelect>
 * that renders the app's styled react-select instead (so the open dropdown matches
 * the rest of the UI instead of the OS-native menu).
 *
 * Keeps the native API so existing call sites barely change:
 *   • children are <option> elements (the first empty/disabled one becomes the placeholder)
 *   • `value` is a string
 *   • `onChange` receives an event-like `{ target: { value } }`
 *
 * Pass-through props (label, required, isDisabled, etc.) go to the styled FormSelect.
 */
const StyledSelect = ({ children, value, onChange, placeholder, ...rest }) => {
    const { t } = useTranslation();
    const opts = React.Children.toArray(children).filter((c) => c && c.type === 'option');

    const options = opts
        .filter((c) => c.props.value !== '' && !c.props.disabled)
        .map((c) => ({ value: c.props.value, label: c.props.children }));

    const ph = placeholder
        ?? opts.find((c) => c.props.value === '' || c.props.disabled)?.props.children
        ?? t('styledSelect.placeholder', 'Select…');

    const selected = options.find((o) => String(o.value) === String(value ?? '')) || null;

    return (
        <FormSelect
            {...rest}
            value={selected}
            options={options}
            placeholder={ph}
            onChange={(opt) => onChange && onChange({ target: { value: opt ? opt.value : '' } })}
        />
    );
};

export default StyledSelect;
